import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { User } from '../types';
import { downloadCSV } from '../utils/export';

export default function Users() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterStation, setFilterStation] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showCsv, setShowCsv] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);

  const fetchUsers = async () => {
    try {
      const data = await api.get<User[]>('/users');
      setUsers(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  // Get unique stations for filter dropdown
  const stations = Array.from(new Set(users.map(u => u.station).filter(Boolean))).sort() as string[];

  const filteredUsers = users.filter(u => {
    if (filterRole && u.role !== filterRole) return false;
    if (filterStatus === 'active' && !u.is_active) return false;
    if (filterStatus === 'inactive' && u.is_active) return false;
    if (filterStation && u.station !== filterStation) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase().trim();
    return (
      `${u.first_name} ${u.last_name}`.toLowerCase().includes(q) ||
      u.email.toLowerCase().includes(q) ||
      (u.badge_number || '').toLowerCase().includes(q)
    );
  });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0, color: '#111' }}>Users</h1>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {filteredUsers.length > 0 && (
            <button onClick={() => {
              const headers = ['First Name', 'Last Name', 'Email', 'Role', 'Badge Number', 'Station', 'Gear Count', 'Status'];
              const rows = filteredUsers.map(u => [u.first_name, u.last_name, u.email, u.role, u.badge_number || '', u.station || '', String(u.gear_count || 0), u.is_active ? 'Active' : 'Inactive']);
              downloadCSV(headers, rows, 'users.csv');
            }}
              style={{ background: '#f3f4f6', border: '1px solid #d1d5db', padding: '10px 16px', borderRadius: 8, fontSize: 14, cursor: 'pointer' }}>
              Export CSV
            </button>
          )}
          <button onClick={() => setShowCsv(true)}
            style={{ background: '#f3f4f6', border: '1px solid #d1d5db', padding: '10px 16px', borderRadius: 8, fontSize: 14, cursor: 'pointer' }}>
            Import CSV
          </button>
          <button onClick={() => setShowInvite(true)}
            style={{ background: '#f3f4f6', border: '1px solid #d1d5db', padding: '10px 16px', borderRadius: 8, fontSize: 14, cursor: 'pointer' }}>
            Invite by Email
          </button>
          <button onClick={() => { setEditingUser(null); setShowForm(true); }}
            style={{ background: '#b91c1c', color: 'white', border: 'none', padding: '10px 20px', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
            + Add User
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginBottom: 12 }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search name, email, badge..."
          style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
        />
        <select value={filterRole} onChange={e => setFilterRole(e.target.value)}
          style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, background: 'white' }}>
          <option value="">All Roles</option>
          <option value="admin">Admin</option>
          <option value="quartermaster">Quartermaster</option>
          <option value="user">Standard User</option>
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, background: 'white' }}>
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        {stations.length > 1 && (
          <select value={filterStation} onChange={e => setFilterStation(e.target.value)}
            style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, background: 'white' }}>
            <option value="">All Stations</option>
            {stations.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        )}
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>Loading...</div>
      ) : (
        <div style={{ background: 'white', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600 }}>Name</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600 }}>Email</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600 }}>Role</th>
                <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600 }}>Gear</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600 }}>Status</th>
                <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(u => (
                <tr key={u.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '12px 16px', fontWeight: 500 }}>{u.first_name} {u.last_name}</td>
                  <td style={{ padding: '12px 16px' }}>{u.email}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600,
                      background: u.role === 'admin' ? '#dbeafe' : u.role === 'quartermaster' ? '#fef3c7' : '#f3f4f6',
                      color: u.role === 'admin' ? '#1d4ed8' : u.role === 'quartermaster' ? '#92400e' : '#374151' }}>
                      {u.role === 'admin' ? 'Admin' : u.role === 'quartermaster' ? 'Quartermaster' : 'User'}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                    <span style={{ fontWeight: 500, color: (u.gear_count || 0) > 0 ? '#111' : '#9ca3af' }}>
                      {u.gear_count || 0}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ color: u.is_active ? '#16a34a' : '#dc2626', fontWeight: 500, fontSize: 13 }}>
                      {u.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      <Link to={`/members/${u.id}/gear`}
                        style={{ background: '#eff6ff', color: '#1e40af', border: 'none', padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 13, textDecoration: 'none', fontWeight: 500 }}>
                        Gear
                      </Link>
                      <button onClick={() => { setEditingUser(u); setShowForm(true); }}
                        style={{ background: '#f3f4f6', border: 'none', padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                        Edit
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredUsers.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ padding: '18px 16px', textAlign: 'center', color: '#6b7280' }}>
                    No users match your search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit User Modal */}
      {showForm && <UserFormModal user={editingUser} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); fetchUsers(); }} />}

      {/* CSV Upload Modal */}
      {showCsv && <CsvUploadModal onClose={() => setShowCsv(false)} onDone={() => { setShowCsv(false); fetchUsers(); }} />}

      {/* Invite User Modal */}
      {showInvite && <InviteModal onClose={() => setShowInvite(false)} />}
    </div>
  );
}

function UserFormModal({ user, onClose, onSaved }: { user: User | null; onClose: () => void; onSaved: () => void }) {
  const isEdit = !!user;
  const [form, setForm] = useState({
    email: user?.email || '',
    firstName: user?.first_name || '',
    lastName: user?.last_name || '',
    password: '',
    role: user?.role || 'user',
    badgeNumber: user?.badge_number || '',
    station: user?.station || '',
    isActive: user?.is_active !== false,
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const update = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const val = e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value;
    setForm(f => ({ ...f, [field]: val }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (isEdit) {
        await api.put(`/users/${user!.id}`, {
          email: form.email,
          firstName: form.firstName,
          lastName: form.lastName,
          role: form.role,
          badgeNumber: form.badgeNumber,
          station: form.station,
          isActive: form.isActive,
        });
        if (form.password) {
          await api.post(`/users/${user!.id}/reset-password`, { password: form.password });
        }
      } else {
        if (!form.password) { setError('Password is required'); setSaving(false); return; }
        await api.post('/users', form);
      }
      onSaved();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' as const };
  const labelStyle: React.CSSProperties = { display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4, color: '#374151' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 16 }}
      onClick={onClose}>
      <div style={{ background: 'white', borderRadius: 12, padding: 24, maxWidth: 480, width: '100%', maxHeight: '90vh', overflow: 'auto' }}
        onClick={e => e.stopPropagation()}>
        <h2 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 600 }}>{isEdit ? 'Edit User' : 'Add User'}</h2>

        {error && <div style={{ background: '#fef2f2', color: '#b91c1c', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 14 }}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>First Name *</label>
              <input value={form.firstName} onChange={update('firstName')} required style={inputStyle} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Last Name *</label>
              <input value={form.lastName} onChange={update('lastName')} required style={inputStyle} />
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Email *</label>
            <input type="email" value={form.email} onChange={update('email')} required style={inputStyle} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Password {isEdit ? '(leave blank to keep current)' : '*'}</label>
            <input type="password" value={form.password} onChange={update('password')} style={inputStyle}
              required={!isEdit} placeholder={isEdit ? 'Leave blank to keep current' : 'Min 10 characters'} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Role</label>
              <select value={form.role} onChange={update('role')} style={{ ...inputStyle, background: 'white' }}>
                <option value="user">Standard User</option>
                <option value="quartermaster">Quartermaster</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Badge #</label>
              <input value={form.badgeNumber} onChange={update('badgeNumber')} style={inputStyle} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Station</label>
              <input value={form.station} onChange={update('station')} style={inputStyle} />
            </div>
          </div>
          {isEdit && (
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.isActive} onChange={update('isActive')} />
                Active account
              </label>
              {!form.isActive && user?.is_active && (user?.gear_count || 0) > 0 && (
                <div style={{ marginTop: 8, padding: '10px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, fontSize: 13, color: '#92400e', lineHeight: 1.4 }}>
                  <strong>Warning:</strong> This user has {user.gear_count} active gear item{user.gear_count !== 1 ? 's' : ''} assigned. Consider reassigning or retiring their gear before deactivating their account.
                </div>
              )}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={{ padding: '10px 20px', border: '1px solid #d1d5db', borderRadius: 8, background: 'white', cursor: 'pointer', fontSize: 14 }}>Cancel</button>
            <button type="submit" disabled={saving}
              style={{ padding: '10px 20px', background: '#b91c1c', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600, opacity: saving ? 0.7 : 1 }}>
              {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Add User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CsvUploadModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<any>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const data = await api.uploadFile('/users/csv-upload', file);
      setResult(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 16 }}
      onClick={onClose}>
      <div style={{ background: 'white', borderRadius: 12, padding: 24, maxWidth: 500, width: '100%' }}
        onClick={e => e.stopPropagation()}>
        <h2 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 600 }}>Import Users from CSV</h2>

        <div style={{ padding: 12, background: '#f3f4f6', borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
          <strong>CSV columns:</strong> email, first_name, last_name, password (optional, defaults to "changeme123"), role (optional), badge_number (optional), station (optional)
        </div>

        {error && <div style={{ background: '#fef2f2', color: '#b91c1c', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 14 }}>{error}</div>}

        {result ? (
          <div>
            <div style={{ padding: 12, background: '#f0fdf4', borderRadius: 8, marginBottom: 12, fontSize: 14 }}>
              Created: {result.created} | Skipped: {result.skipped}
            </div>
            {result.errors?.length > 0 && (
              <div style={{ maxHeight: 150, overflow: 'auto', fontSize: 13, color: '#666', marginBottom: 12 }}>
                {result.errors.map((e: string, i: number) => <div key={i}>{e}</div>)}
              </div>
            )}
            <button onClick={onDone}
              style={{ width: '100%', padding: '10px', background: '#b91c1c', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
              Done
            </button>
          </div>
        ) : (
          <>
            <input type="file" accept=".csv" onChange={e => setFile(e.target.files?.[0] || null)}
              style={{ marginBottom: 16, fontSize: 14 }} />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={onClose} style={{ padding: '10px 20px', border: '1px solid #d1d5db', borderRadius: 8, background: 'white', cursor: 'pointer', fontSize: 14 }}>Cancel</button>
              <button onClick={handleUpload} disabled={!file || uploading}
                style={{ padding: '10px 20px', background: '#b91c1c', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600, opacity: (!file || uploading) ? 0.7 : 1 }}>
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function InviteModal({ onClose }: { onClose: () => void }) {
  const [emails, setEmails] = useState('');
  const [role, setRole] = useState('user');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState<Array<{ email: string; status: 'sent' | 'error'; message: string }>>([]);

  const inputStyle = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' as const };
  const labelStyle: React.CSSProperties = { display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4, color: '#374151' };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    setError('');
    setResults([]);

    // Parse emails: split by newline, comma, or semicolon
    const emailList = emails
      .split(/[\n,;]+/)
      .map(e => e.trim().toLowerCase())
      .filter(e => e.length > 0);

    if (emailList.length === 0) {
      setError('Please enter at least one email address');
      setSending(false);
      return;
    }

    const inviteResults: Array<{ email: string; status: 'sent' | 'error'; message: string }> = [];

    for (const email of emailList) {
      try {
        const data: any = await api.post('/auth/invite', { email, role });
        inviteResults.push({ email, status: 'sent', message: data.message || 'Invitation sent' });
      } catch (err: any) {
        inviteResults.push({ email, status: 'error', message: err.message || 'Failed to send' });
      }
    }

    setResults(inviteResults);
    setEmails('');
    setSending(false);
  };

  const sentCount = results.filter(r => r.status === 'sent').length;
  const errorCount = results.filter(r => r.status === 'error').length;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 16 }}
      onClick={onClose}>
      <div style={{ background: 'white', borderRadius: 12, padding: 24, maxWidth: 480, width: '100%', maxHeight: '90vh', overflow: 'auto' }}
        onClick={e => e.stopPropagation()}>
        <h2 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 600 }}>Invite Users by Email</h2>
        <p style={{ color: '#6b7280', fontSize: 13, marginBottom: 16, lineHeight: 1.4 }}>
          Send email invitations to join your department. Enter one email per line, or separate with commas.
        </p>

        {error && <div style={{ background: '#fef2f2', color: '#b91c1c', padding: '10px 14px', borderRadius: 8, marginBottom: 12, fontSize: 14 }}>{error}</div>}

        {results.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 8, fontSize: 14,
              background: errorCount === 0 ? '#f0fdf4' : '#fffbeb',
              color: errorCount === 0 ? '#166534' : '#92400e' }}>
              {sentCount} sent{errorCount > 0 ? `, ${errorCount} failed` : ''}
            </div>
            <div style={{ maxHeight: 150, overflow: 'auto', fontSize: 13 }}>
              {results.map((r, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #f3f4f6' }}>
                  <span style={{ color: '#374151' }}>{r.email}</span>
                  <span style={{ color: r.status === 'sent' ? '#16a34a' : '#dc2626', fontWeight: 500 }}>
                    {r.status === 'sent' ? 'Sent' : r.message}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <form onSubmit={handleInvite}>
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Email Addresses *</label>
            <textarea value={emails} onChange={e => setEmails(e.target.value)} required
              rows={4} style={{ ...inputStyle, resize: 'vertical' }}
              placeholder={"firefighter1@dept.com\nfirefighter2@dept.com\nfirefighter3@dept.com"} />
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Role (applied to all)</label>
            <select value={role} onChange={e => setRole(e.target.value)} style={{ ...inputStyle, background: 'white' }}>
              <option value="user">Standard User</option>
              <option value="quartermaster">Quartermaster</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose}
              style={{ padding: '10px 20px', border: '1px solid #d1d5db', borderRadius: 8, background: 'white', cursor: 'pointer', fontSize: 14 }}>
              Close
            </button>
            <button type="submit" disabled={sending}
              style={{ padding: '10px 20px', background: '#b91c1c', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600, opacity: sending ? 0.7 : 1 }}>
              {sending ? 'Sending...' : 'Send Invitations'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
